import { getTestResultsService } from '@/api/services/testService'
import { ResultPreview } from '@/api/services/types/resultsTypes'
import { Skeleton } from '@/components/skeleton'
import { Button } from '@/components/ui/common/Button'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

interface TestHistoryProps {
  onViewDetails?: (testResultId: string) => void
}

const ITEMS_PER_PAGE = 20

export default function ResultsHistory({ onViewDetails }: TestHistoryProps) {
  const [currentPage, setCurrentPage] = useState(1)

  const { data, isLoading, error } = useQuery({
    queryKey: ['test-results', currentPage],
    queryFn: async () => {
      const response = await getTestResultsService({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE
      })
      if (!response.success) {
        console.error('Błąd API:', response.data)
        return null
      }
      return response.data
    }
  })

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="mb-4 h-8 w-1/3" />
        <Skeleton className="mb-2 h-4 w-1/2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <div className="text-red-500">
          Wystąpił błąd podczas ładowania historii testów
        </div>
      </div>
    )
  }
  const defaultTestResults: ResultPreview[] = [
    {
      id: '0KXCQYQMDQVJP',
      test: {
        id: '0KX36ENVC4D3P',
        purpose: 'Test Pamięci Operacyjnej',
        constraints: 'fast',
        procedure: 'fast',
        recommendations: 'fast',
        createdAt: '2025-06-02T09:37:24.443773Z',
        updatedAt: '2025-06-02T09:37:24.443773Z'
      },
      userId: '0KT8975WRJ2DP',
      createdAt: '2025-06-03T07:52:09.891784Z'
    },
    {
      id: '0KXCQYQMDQVJQ',
      test: {
        id: '0KX36ENVC4D3Q',
        purpose: 'Test Dopasowania Grafiki',
        constraints: 'medium',
        procedure: 'visual',
        recommendations: 'detailed',
        createdAt: '2025-05-10T09:37:24.443773Z',
        updatedAt: '2025-06-02T09:37:24.443773Z'
      },
      userId: '0KT8975WRJ2DP',
      createdAt: '2025-05-10T07:52:09.891784Z'
    },
    {
      id: '0KXCQYQMDQVJR',
      test: {
        id: '0KX36ENVC4D3R',
        purpose: 'Test Wielokrotnego Wyboru',
        constraints: 'slow',
        procedure: 'multiple-choice',
        recommendations: 'comprehensive',
        updatedAt: '2025-06-02T09:37:24.443773Z',
        createdAt: '2025-05-05T09:37:24.443773Z'
      },
      userId: '0KT8975WRJ2DP',
      createdAt: '2025-05-05T07:52:09.891784Z'
    }
  ]

  const displayTestResults = data?.items || defaultTestResults
  const totalPages = data?.total
    ? Math.ceil(data.total / ITEMS_PER_PAGE)
    : Math.ceil(defaultTestResults.length / ITEMS_PER_PAGE)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const handleViewDetails = (testResultId: string) => {
    if (onViewDetails) {
      onViewDetails(testResultId)
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="bg-white p-8 shadow-sm">
        <h3 className="mb-6 text-2xl font-bold">Rezultaty z testów</h3>

        <div className="overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  NAZWA TESTU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  DATA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase">
                  AKCJE
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {displayTestResults.map((testResult) => (
                <tr className="hover:bg-gray-50" key={testResult.id}>
                  <td className="max-w-[75vw] px-6 py-4 whitespace-normal">
                    <div className="max-w-[200px] truncate text-sm font-medium break-words text-gray-900 sm:max-w-[300px] md:max-w-[400px]">
                      {testResult.test.purpose}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {formatDate(testResult.createdAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <Button
                      className="hover:[#1b596e] h-auto p-0 font-normal text-[#2db2b8]"
                      onClick={() => handleViewDetails(testResult.id)}
                      variant="link"
                    >
                      Szczegóły
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        { totalPages !== 1 && <div className="mt-4 flex justify-center gap-2">
          <Button
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            variant="outline"
          >
            Poprzednia
          </Button>
          <span className="flex items-center px-4">
            Strona {currentPage} z {totalPages}
          </span>
          <Button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            variant="outline"
          >
            Następna
          </Button>
        </div>}
        
      </div>
    </div>
  )
}
